import React, { useState, useEffect, useRef } from "react";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator";
import { DownArrow } from "../icons";
import "./glowYearDropdown.scss";

function GlowYearSelect({
  value,
  onChange,
  options,
  isOpen,
  onToggle,
  currentContextType,
  fieldError,
  className,
  // Removed resetYearDropdown and onResetYearComplete per your feedback
}) {
  const storageKey = getStorageKey("ccExpYear", currentContextType);
  const [selectedYear, setSelectedYear] = useState(value);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const [glow, setGlow] = useState(false);
  const optionClickedRef = useRef(false);

  const closeDropdown = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      onToggle(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", closeDropdown);
    return () => {
      document.removeEventListener("click", closeDropdown);
    };
  }, [onToggle]);

  useEffect(() => {
    if (searchTerm.length > 0) {
      const index = options.findIndex((year) =>
        new RegExp(`^${searchTerm}`, "i").test(year.toString())
      );
      if (index !== -1) {
        setFocusedIndex(index);
      }
    }
  }, [searchTerm, options]);

  const handleDropdownKeyNavigation = (e) => {
    const updateIndex = (currentIndex, increment) => {
      const newIndex = currentIndex + increment;
      return newIndex < 0
        ? options.length - 1
        : newIndex >= options.length
          ? 0
          : newIndex;
    };

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex(updateIndex(focusedIndex, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(updateIndex(focusedIndex, -1));
    } else if (e.key === "Enter") {
      const selected = options[focusedIndex];
      handleYearChange(selected);
      onToggle(false);
    } else if (/^[0-9]$/.test(e.key)) {
      setSearchTerm((prev) => prev + e.key);
      const index = options.findIndex((year) =>
        new RegExp(`^${searchTerm}`, "i").test(year.toString())
      );
      if (index !== -1) {
        setFocusedIndex(index);
      }
      setTimeout(() => {
        setSearchTerm("");
      }, 1000);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    onChange({ target: { value: year } });
    localStorage.setItem(storageKey, year);
  };

  const handleMouseOver = (index) => {
    if (!isKeyboardNavigation) {
      setFocusedIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (!isKeyboardNavigation) {
      setFocusedIndex(-1);
    }
  };

  // Function to clear the selected year
  const clearYear = (e) => {
    e.stopPropagation();
    if (selectedYear !== "") {
      setSelectedYear("");
      onChange({ target: { value: "" } });
      localStorage.removeItem(storageKey);
    }
  };

  // When the container is clicked, trigger the glow and then toggle the dropdown.
  const handleContainerClick = () => {
    setGlow(true);
    const newOpenState = !isOpen;
    setTimeout(() => {
      setGlow(false);
      onToggle(newOpenState);
    }, 200); // Adjust the delay as needed
  };

  const containerClasses = `glow-year-select ${className} ${
    glow ? "glow-on-animation show-animation" : ""
  }`;

  return (
    <div
      className={containerClasses}
      ref={dropdownRef}
      onKeyDown={handleDropdownKeyNavigation}
      onClick={handleContainerClick} // Whole container toggles the dropdown
    >
      <div className="glow-year-select-header">
        <div className="glow-year-select-content">
          {!selectedYear ? (
            <div className="year-placeholder">
              {fieldError ? fieldError : "Select an Expired Year"}
            </div>
          ) : (
            <div className="selected-year">{selectedYear}</div>
          )}
          <DownArrow fillColor="var(--color-bloodred)" />
        </div>
      </div>
      {isOpen && (
        <div className="year-dropdown-options">
          {options.map((year, index) => (
            <div
              key={year}
              className={index === focusedIndex ? "focused" : ""}
              onClick={() => {
                optionClickedRef.current = true;
                handleYearChange(year);
                onToggle(false);
              }}
              onMouseOver={() => handleMouseOver(index)}
              onMouseLeave={handleMouseLeave}
              tabIndex={-1}
              ref={(el) => {
                if (focusedIndex === index && el) el.focus();
              }}
            >
              {year}
            </div>
          ))}
        </div>
      )}
      {selectedYear && (
        <button type="button" onClick={clearYear} className="clear-yr-btn">
          Clear
        </button>
      )}
    </div>
  );
}

export default GlowYearSelect;
